import {
  NativeInterfaceSettings,
  readInterfacePreferences,
} from "./native-interface-settings";
import { NativeCompactProperties } from "./native-compact-properties";
import type { SceneNode } from "../scene/model";
const preferenceKey = "creatorenginepro-native-ui-v1";
/** View-only shell: it never owns scene values, history or project persistence. */
export class NativeChrome {
  private preferences = readInterfacePreferences();
  private settings: NativeInterfaceSettings;
  private narrow = matchMedia("(max-width: 1100px)");
  private visible = { left: true, right: true };
  private compact = new NativeCompactProperties();
  private dialog = document.getElementById(
    "native-settings-dialog",
  ) as HTMLDialogElement;
  constructor(
    private root: HTMLElement,
    cancelGesture: () => void,
    changed: () => void,
    workspaceColor: () => string,
  ) {
    for (const side of ["left", "right"] as const)
      document.getElementById("native-toggle-" + side)!.onclick = () => {
        this.visible[side] = !this.visible[side];
        if (this.narrow.matches && this.visible[side])
          this.visible[side === "left" ? "right" : "left"] = false;
        if (!this.narrow.matches) {
          this.preferences[side] = this.visible[side];
          this.savePreferences();
        }
        this.applyPanels();
      };
    const responsive = () => {
      this.visible = this.narrow.matches
        ? { left: false, right: false }
        : {
            left: this.preferences.left,
            right: this.preferences.right,
          };
      this.closePopup();
      this.applyPanels();
    };
    this.narrow.addEventListener("change", responsive);
    responsive();
    document.getElementById("native-settings")!.onclick = () => {
      cancelGesture();
      this.closePopup();
      this.settings.open();
    };
    document.getElementById("native-settings-close")!.onclick = () =>
      this.dialog.close();
    for (const button of root.querySelectorAll<HTMLButtonElement>(
      "[data-native-theme]",
    ))
      button.onclick = () => {
        this.preferences.theme = button.dataset.nativeTheme as "light" | "dark";
        this.applyTheme();
        this.savePreferences();
      };
    this.applyTheme();
    this.settings = new NativeInterfaceSettings(
      root,
      this.preferences,
      () => {
        this.savePreferences();
        changed();
      },
      workspaceColor,
    );
  }
  get workspaceColor() {
    return this.preferences.workspace;
  }
  get workspaceGrid() {
    return this.preferences.workspaceGrid;
  }
  get wheelZoomMode() {
    return this.preferences.wheel;
  }
  clearWorkspaceOverride() {
    this.settings.clearWorkspaceOverride();
  }
  renderIcons() {
    this.settings.render();
  }
  get modal() {
    return !!document.querySelector(
      "dialog[open], .clipart-picker-overlay.open",
    );
  }
  closePopup() {
    return this.compact.closePopup();
  }
  render(nodes: SceneNode[]) {
    this.compact.render(nodes);
  }
  /** Only explicit Fit/zoom uses panel insets. Toggling panels never changes camera. */
  workArea() {
    const left =
      !this.narrow.matches && this.visible.left
        ? document.getElementById("native-left-panel")!.offsetWidth
        : 0;
    const right =
      !this.narrow.matches && this.visible.right
        ? document.getElementById("native-right-panel")!.offsetWidth
        : 0;
    return {
      x: left + 38,
      y: 64,
      width: Math.max(80, innerWidth - left - right - 76),
      height: Math.max(80, innerHeight - 214),
    };
  }
  private applyPanels() {
    for (const side of ["left", "right"] as const) {
      this.root.dataset[side] = this.visible[side] ? "open" : "closed";
      document.getElementById("native-" + side + "-panel")!.inert =
        !this.visible[side];
      const button = document.getElementById("native-toggle-" + side)!;
      button.setAttribute("aria-expanded", String(this.visible[side]));
      const text =
        (this.visible[side] ? "Скрыть " : "Показать ") +
        (side === "left" ? "слои" : "свойства");
      button.setAttribute("aria-label", text);
      button.title = text;
    }
  }
  private applyTheme() {
    this.root.dataset.theme = this.preferences.theme;
    for (const input of this.root.querySelectorAll<HTMLInputElement>(
      '[name="creator-sidebar-theme"]',
    ))
      input.checked = input.value === this.preferences.theme;
    for (const button of this.root.querySelectorAll<HTMLButtonElement>(
      "[data-native-theme]",
    ))
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.nativeTheme === this.preferences.theme),
      );
  }
  private savePreferences() {
    try {
      localStorage.setItem(preferenceKey, JSON.stringify(this.preferences));
    } catch {
      /* Preferences are optional, unlike a saved project. */
    }
  }
}
